/**
 * WHETHER ANALYTICS MAY BE CONSIDERED AT ALL.
 *
 * Every gate here is an AND, every one of them is checked before any Google
 * resource is referenced, and failing any of them produces the same result: a
 * no-op that loads nothing and sends nothing. There is no partial state, no
 * "configured but idle" mode, and no path where a missing value is treated as
 * permission.
 *
 * THE FAILURE THIS SHAPE EXISTS TO PREVENT. The usual analytics bug is not a
 * wrong event — it is a build that was never meant to measure quietly
 * measuring, because someone set a measurement id in a config file and the
 * library treated its presence as the switch. A measurement id is an ADDRESS.
 * It says where data would go if any were sent. It is not consent, it is not
 * an environment, and it is not a decision, so on its own it does nothing
 * here.
 *
 * WHAT IS DELIBERATELY NOT A GATE: a Firebase session, an anonymous uid, a
 * previous navigation, anything stored by another origin, and any value
 * carried in a URL. None of those is an answer from the person in front of the
 * page, and treating any of them as one is how a surface ends up collecting
 * from someone who declined somewhere else.
 */
import { config } from "../config/env";
import { ELIGIBLE_HOSTNAME, privacyNoticeUrl, resolveMeasurementId } from "./property";

export { ELIGIBLE_HOSTNAME };

export type GateFailure =
  | "not-production"
  | "wrong-hostname"
  | "not-enabled"
  | "no-privacy-notice"
  | "no-measurement-id"
  | "malformed-measurement-id"
  | "measurement-id-is-firebase";

export type GateResult =
  | { readonly eligible: true; readonly measurementId: string }
  | { readonly eligible: false; readonly failures: readonly GateFailure[] };

/**
 * `hostname` is a parameter rather than a read of `window`.
 *
 * Not for testability — for honesty. This module must be callable from a
 * context that has already decided WHICH hostname matters, and a function that
 * reaches for a global cannot be reasoned about at the call site. The caller
 * passes `window.location.hostname`; a test passes what it means.
 */
export function evaluateGates(hostname: string): GateResult {
  const failures: GateFailure[] = [];
  /*
   * READ DIRECTLY, AND THIS IS THE ONE MODULE BESIDES config/env.ts THAT DOES.
   *
   * `config/env.ts` is the single typed accessor precisely so a missing value
   * fails loudly instead of surfacing as `undefined` — its `required()`
   * THROWS. Both values here are optional by design: absent is the committed
   * state and absent means off, so routing them through an accessor that
   * refuses to boot without them would turn "analytics are not configured"
   * into "the booking page does not load".
   */
  const env = import.meta.env;

  if (config.env !== "prod") failures.push("not-production");
  if (hostname !== ELIGIBLE_HOSTNAME) failures.push("wrong-hostname");

  // EXACTLY the string "true". Not truthiness: every Vite env value is a
  // string, so "false", "0" and "no" are all truthy and every one of them
  // would arm collection under a truthiness check.
  if (env.VITE_BOOK_GA4_ENABLED !== "true") failures.push("not-enabled");

  /*
   * NO PUBLISHED PRIVACY NOTICE, NO COLLECTION AND NO REQUEST.
   *
   * A build with the flag ON and no valid URL never gets here — `property.ts`
   * throws at boot. This branch is the committed state: flag off, no URL, and
   * the refusal is recorded so the operator checklist can distinguish "nobody
   * turned it on" from "Legal has not answered yet". The consent UI reads the
   * same value and declines to ask a question whose terms it cannot show.
   */
  if (privacyNoticeUrl() === null) failures.push("no-privacy-notice");

  const property = resolveMeasurementId();
  if (!property.ok) failures.push(...property.failures);

  if (failures.length > 0) return { eligible: false, failures };
  return { eligible: true, measurementId: (property as { measurementId: string }).measurementId };
}
